import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus,
  PencilSimple,
  CheckCircle,
  XCircle,
  X,
  UsersThree,
  MagnifyingGlass,
  UserCircle,
  ShieldCheck,
  ArrowLeft,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "../lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import ConfirmModal from "@/components/ConfirmModal";

const GROUP_COLORS = [
  {
    value: "bg-blue-100 text-blue-700",
    label: "Azul",
    dot: "bg-blue-500",
  },
  {
    value: "bg-green-100 text-green-700",
    label: "Verde",
    dot: "bg-green-500",
  },
  {
    value: "bg-amber-100 text-amber-700",
    label: "Ámbar",
    dot: "bg-amber-500",
  },
  {
    value: "bg-purple-100 text-purple-700",
    label: "Morado",
    dot: "bg-purple-500",
  },
  {
    value: "bg-pink-100 text-pink-700",
    label: "Rosa",
    dot: "bg-pink-500",
  },
  {
    value: "bg-red-100 text-red-700",
    label: "Rojo",
    dot: "bg-red-500",
  },
  {
    value: "bg-cyan-100 text-cyan-700",
    label: "Cian",
    dot: "bg-cyan-500",
  },
  {
    value: "bg-gray-100 text-gray-700",
    label: "Gris",
    dot: "bg-gray-500",
  },
];

const emptyGroup = () => ({
  name: "",
  description: "",
  color: "bg-blue-100 text-blue-700",
  active: true,
});

export default function Grupos() {
  const { user: me, can } = useAuth();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modal, setModal] = useState(null);
  const [activeTarget, setActiveTarget] = useState(null);

  const [usersModal, setUsersModal] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [originalUserIds, setOriginalUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [savingUsers, setSavingUsers] = useState(false);

  /*
   * ============================================================
   * DATOS
   * ============================================================
   */

  const fetchGroups = async () => {
    try {
      setLoading(true);

      const { data } = await api.get("/grupos");

      setGroups(data || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se han podido cargar los grupos",
      );

      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  /*
   * ============================================================
   * HELPERS
   * ============================================================
   */

  const getColor = (colorValue) =>
    GROUP_COLORS.find((color) => color.value === colorValue) || GROUP_COLORS[0];

  const getUserName = (user) => {
    const fallero = user?.fallero;

    const fullName = [fallero?.nombre, fallero?.apellidos]
      .filter(Boolean)
      .join(" ")
      .trim();

    return fullName || user?.email || "Usuario";
  };

  /*
   * ============================================================
   * CREAR / EDITAR GRUPO
   * ============================================================
   */

  const openCreateModal = () => {
    setModal({
      isEdit: false,
      data: emptyGroup(),
    });
  };

  const openEditModal = (group) => {
    setModal({
      isEdit: true,
      data: {
        id: group.id,
        name: group.name || "",
        description: group.description || "",
        color: group.color || "bg-blue-100 text-blue-700",
        active: group.active !== false,
      },
    });
  };

  const closeModal = () => {
    setModal(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!modal?.data?.name?.trim()) {
      toast.error("El nombre del grupo es obligatorio");
      return;
    }

    try {
      const payload = {
        name: modal.data.name.trim(),
        description: modal.data.description?.trim() || "",
        color: modal.data.color,
        active: modal.data.active,
      };

      if (modal.isEdit) {
        await api.put(`/grupos/${modal.data.id}`, payload);
        toast.success("Grupo actualizado");
      } else {
        await api.post("/grupos", payload);
        toast.success("Grupo creado");
      }

      closeModal();
      await fetchGroups();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "No se ha podido guardar el grupo",
      );
    }
  };

  /*
   * ============================================================
   * ACTIVAR / DESACTIVAR
   * ============================================================
   */

  const toggleGroupActive = async (group) => {
    const newActive = !group.active;

    try {
      await api.put(`/grupos/${group.id}`, {
        active: newActive,
      });

      toast.success(newActive ? "Grupo activado" : "Grupo desactivado");

      await fetchGroups();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "No se ha podido actualizar el grupo",
      );
    }
  };

  /*
   * ============================================================
   * USUARIOS DEL GRUPO
   * ============================================================
   */

  const openUsersModal = async (group) => {
    setUsersModal({
      ...group,
    });

    setUsers([]);
    setSelectedUserIds([]);
    setOriginalUserIds([]);
    setUserSearch("");
    setLoadingUsers(true);

    try {
      const [{ data: groupUserIds }, { data: tenantUsers }] = await Promise.all(
        [api.get(`/grupos/${group.id}/users`), api.get("/users/tenant_users")],
      );

      const normalizedGroupUserIds = groupUserIds || [];
      const normalizedUsers = tenantUsers || [];

      setUsers(normalizedUsers);
      setSelectedUserIds(normalizedGroupUserIds);
      setOriginalUserIds(normalizedGroupUserIds);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "No se han podido cargar los usuarios del grupo",
      );

      setUsersModal(null);
    } finally {
      setLoadingUsers(false);
    }
  };

  const closeUsersModal = () => {
    if (savingUsers) return;

    setUsersModal(null);
    setUsers([]);
    setSelectedUserIds([]);
    setOriginalUserIds([]);
    setUserSearch("");
  };

  const toggleUser = (userId) => {
    setSelectedUserIds((current) => {
      if (current.includes(userId)) {
        return current.filter((id) => id !== userId);
      }

      return [...current, userId];
    });
  };

  const selectAllFilteredUsers = () => {
    setSelectedUserIds((current) => {
      const currentSet = new Set(current);

      filteredUsers.forEach((user) => {
        currentSet.add(user.id);
      });

      return [...currentSet];
    });
  };

  const deselectAllFilteredUsers = () => {
    const filteredIds = new Set(filteredUsers.map((user) => user.id));

    setSelectedUserIds((current) =>
      current.filter((id) => !filteredIds.has(id)),
    );
  };

  const saveGroupUsers = async () => {
    if (!usersModal) return;

    const originalSet = new Set(originalUserIds);
    const selectedSet = new Set(selectedUserIds);

    const added = [...selectedSet].filter((id) => !originalSet.has(id));

    const removed = [...originalSet].filter((id) => !selectedSet.has(id));

    if (!added.length && !removed.length) {
      closeUsersModal();
      return;
    }

    setSavingUsers(true);

    try {
      await Promise.all([
        ...added.map((userId) =>
          api.post(`/grupos/${usersModal.id}/users/${userId}`),
        ),

        ...removed.map((userId) =>
          api.delete(`/grupos/${usersModal.id}/users/${userId}`),
        ),
      ]);

      toast.success("Usuarios del grupo actualizados");

      setUsersModal(null);
      setUsers([]);
      setSelectedUserIds([]);
      setOriginalUserIds([]);
      setUserSearch("");

      await fetchGroups();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "No se han podido actualizar los usuarios del grupo",
      );
    } finally {
      setSavingUsers(false);
    }
  };

  /*
   * ============================================================
   * FILTRO DE USUARIOS
   * ============================================================
   */

  const filteredUsers = useMemo(() => {
    const search = userSearch.trim().toLowerCase();

    if (!search) return users;

    return users.filter((user) => {
      const text = [
        user?.fallero?.nombre,
        user?.fallero?.apellidos,
        user?.fallero?.email,
        user?.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(search);
    });
  }, [users, userSearch]);

  /*
   * ============================================================
   * PERMISOS
   * ============================================================
   */

  if (me && !can(PERMISSIONS.MANAGE_GROUPS)) {
    return (
      <div className="p-10 text-center">
        <ShieldCheck
          size={48}
          className="mx-auto text-med-muted mb-4"
          weight="duotone"
        />

        <h2 className="font-display text-2xl font-bold text-med-text mb-2">
          Acceso restringido
        </h2>

        <p className="text-med-muted">
          No tienes permiso para gestionar grupos.
        </p>
      </div>
    );
  }

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div className="p-6 md:p-10" data-testid="grupos-page">
      <Link
        to="/usuarios"
        data-testid="back-to-usuarios"
        className="inline-flex items-center gap-1.5 text-sm text-med-muted hover:text-med-text mb-6"
      >
        <ArrowLeft size={14} weight="bold" />
        Volver a usuarios
      </Link>
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Administración
          </div>

          <h1 className="font-display text-4xl font-bold text-med-text">
            Grupos
          </h1>

          <p className="text-med-muted mt-1">
            Organiza a los usuarios en grupos para utilizarlos como
            destinatarios en diferentes funcionalidades.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-med-primary px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-med-primary/90"
        >
          <Plus size={20} weight="bold" />
          Nuevo grupo
        </button>
      </header>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-44 rounded-3xl border border-med-border bg-white animate-pulse"
            />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-3xl border border-med-border bg-white p-12 text-center">
          <UsersThree
            size={52}
            weight="duotone"
            className="mx-auto text-med-muted mb-4"
          />

          <h2 className="font-display text-xl font-bold text-med-text">
            Todavía no hay grupos
          </h2>

          <p className="text-med-muted mt-2 max-w-md mx-auto">
            Crea el primer grupo para poder organizar a los usuarios de la falla
            y utilizar estos grupos en notificaciones y otras funcionalidades.
          </p>

          <button
            type="button"
            onClick={openCreateModal}
            className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-med-primary px-5 py-3 text-sm font-semibold text-white hover:bg-med-primary/90"
          >
            <Plus size={19} weight="bold" />
            Crear primer grupo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {groups.map((group) => {
            const color = getColor(group.color);

            return (
              <motion.div
                key={group.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-3xl border border-med-border bg-white p-6 shadow-sm ${
                  !group.active ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`w-11 h-11 shrink-0 rounded-2xl flex items-center justify-center ${color.value}`}
                    >
                      <UsersThree size={23} weight="duotone" />
                    </div>

                    <div className="min-w-0">
                      <h2 className="font-display text-lg font-bold text-med-text truncate">
                        {group.name}
                      </h2>

                      {!group.active && (
                        <span className="inline-flex mt-1 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-500">
                          Inactivo
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${color.value}`}
                  >
                    {group.user_count || 0}{" "}
                    {group.user_count === 1 ? "usuario" : "usuarios"}
                  </div>
                </div>

                <p className="mt-4 min-h-[42px] text-sm text-med-muted">
                  {group.description || "Sin descripción"}
                </p>

                <div className="mt-6 pt-4 border-t border-med-border flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => openUsersModal(group)}
                    disabled={!group.active}
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-med-primary transition-colors hover:bg-med-primary/10 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <UsersThree size={18} weight="duotone" />
                    Usuarios
                  </button>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openEditModal(group)}
                      className="p-2 rounded-xl text-med-text hover:bg-med-surface2 transition-colors"
                      title="Editar grupo"
                    >
                      <PencilSimple size={18} weight="duotone" />
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTarget(group)}
                      className={`p-2 rounded-xl transition-colors ${
                        group.active
                          ? "text-med-danger hover:bg-med-danger/10"
                          : "text-med-success hover:bg-med-success/10"
                      }`}
                      title={
                        group.active ? "Desactivar grupo" : "Activar grupo"
                      }
                    >
                      {group.active ? (
                        <XCircle size={18} weight="duotone" />
                      ) : (
                        <CheckCircle size={18} weight="duotone" />
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ========================================================
          MODAL CREAR / EDITAR
      ======================================================== */}

      {modal && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.95,
              y: 10,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            className="w-full max-w-lg rounded-3xl border border-med-border bg-white shadow-2xl"
          >
            <form onSubmit={handleSubmit}>
              <div className="flex items-start justify-between gap-4 p-6 border-b border-med-border">
                <div>
                  <h2 className="font-display text-2xl font-bold text-med-text">
                    {modal.isEdit ? "Editar grupo" : "Nuevo grupo"}
                  </h2>

                  <p className="text-sm text-med-muted mt-1">
                    Define el nombre y la apariencia del grupo.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeModal}
                  className="p-2 rounded-xl text-med-muted hover:bg-med-surface2 hover:text-med-text"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-med-text mb-2">
                    Nombre
                  </label>

                  <input
                    type="text"
                    value={modal.data.name}
                    onChange={(event) =>
                      setModal((current) => ({
                        ...current,
                        data: {
                          ...current.data,
                          name: event.target.value,
                        },
                      }))
                    }
                    placeholder="Ej. Junta Directiva"
                    autoFocus
                    className="w-full rounded-2xl border border-med-border bg-white px-4 py-3 text-sm text-med-text outline-none transition-colors focus:border-med-primary focus:ring-2 focus:ring-med-primary/10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-med-text mb-2">
                    Descripción
                  </label>

                  <textarea
                    value={modal.data.description}
                    onChange={(event) =>
                      setModal((current) => ({
                        ...current,
                        data: {
                          ...current.data,
                          description: event.target.value,
                        },
                      }))
                    }
                    placeholder="Describe para qué se utiliza este grupo..."
                    rows={3}
                    className="w-full resize-none rounded-2xl border border-med-border bg-white px-4 py-3 text-sm text-med-text outline-none transition-colors focus:border-med-primary focus:ring-2 focus:ring-med-primary/10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-med-text mb-3">
                    Color
                  </label>

                  <div className="flex flex-wrap gap-2">
                    {GROUP_COLORS.map((color) => {
                      const selected = modal.data.color === color.value;

                      return (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() =>
                            setModal((current) => ({
                              ...current,
                              data: {
                                ...current.data,
                                color: color.value,
                              },
                            }))
                          }
                          title={color.label}
                          className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                            selected
                              ? "ring-2 ring-med-primary ring-offset-2"
                              : "hover:scale-105"
                          } ${color.value}`}
                        >
                          <span
                            className={`w-3.5 h-3.5 rounded-full ${color.dot}`}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {modal.isEdit && (
                  <div className="rounded-2xl bg-med-surface2 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-med-text">
                          Estado del grupo
                        </p>

                        <p className="text-xs text-med-muted mt-1">
                          Los grupos inactivos no podrán utilizarse como
                          destinatarios.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setModal((current) => ({
                            ...current,
                            data: {
                              ...current.data,
                              active: !current.data.active,
                            },
                          }))
                        }
                        className={`relative h-7 w-12 rounded-full transition-colors ${
                          modal.data.active ? "bg-med-primary" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                            modal.data.active
                              ? "translate-x-5"
                              : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-6 border-t border-med-border">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-2xl border border-med-border px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-med-surface2"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="rounded-2xl bg-med-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-med-primary/90"
                >
                  {modal.isEdit ? "Guardar cambios" : "Crear grupo"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* ========================================================
          MODAL USUARIOS DEL GRUPO
      ======================================================== */}

      {usersModal && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.95,
              y: 10,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-3xl border border-med-border bg-white shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4 p-6 border-b border-med-border">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-11 h-11 shrink-0 rounded-2xl flex items-center justify-center ${
                      getColor(usersModal.color).value
                    }`}
                  >
                    <UsersThree size={23} weight="duotone" />
                  </div>

                  <div className="min-w-0">
                    <h2 className="font-display text-2xl font-bold text-med-text truncate">
                      {usersModal.name}
                    </h2>

                    <p className="text-sm text-med-muted">
                      Selecciona los usuarios que pertenecen al grupo.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={closeUsersModal}
                disabled={savingUsers}
                className="p-2 rounded-xl text-med-muted hover:bg-med-surface2 hover:text-med-text disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 pb-4">
              <div className="relative">
                <MagnifyingGlass
                  size={19}
                  weight="duotone"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-med-muted"
                />

                <input
                  type="text"
                  value={userSearch}
                  onChange={(event) => setUserSearch(event.target.value)}
                  placeholder="Buscar por nombre o email..."
                  className="w-full rounded-2xl border border-med-border bg-white pl-11 pr-4 py-3 text-sm text-med-text outline-none transition-colors focus:border-med-primary focus:ring-2 focus:ring-med-primary/10"
                />
              </div>

              {!loadingUsers && users.length > 0 && (
                <div className="flex items-center justify-between gap-3 mt-3 text-xs">
                  <span className="text-med-muted">
                    {selectedUserIds.length} de {users.length} usuarios
                    seleccionados
                  </span>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={selectAllFilteredUsers}
                      className="font-semibold text-med-primary hover:underline"
                    >
                      Seleccionar todos
                    </button>

                    <button
                      type="button"
                      onClick={deselectAllFilteredUsers}
                      className="font-semibold text-med-muted hover:text-med-text hover:underline"
                    >
                      Deseleccionar
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
              {loadingUsers ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4].map((item) => (
                    <div
                      key={item}
                      className="h-16 rounded-2xl bg-med-surface2 animate-pulse"
                    />
                  ))}
                </div>
              ) : users.length === 0 ? (
                <div className="rounded-2xl bg-med-surface2 p-8 text-center">
                  <UserCircle
                    size={42}
                    weight="duotone"
                    className="mx-auto text-med-muted mb-3"
                  />

                  <p className="text-sm font-semibold text-med-text">
                    No hay usuarios disponibles
                  </p>

                  <p className="text-xs text-med-muted mt-1">
                    Primero debes crear usuarios desde la sección Usuarios.
                  </p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="rounded-2xl bg-med-surface2 p-8 text-center">
                  <MagnifyingGlass
                    size={38}
                    weight="duotone"
                    className="mx-auto text-med-muted mb-3"
                  />

                  <p className="text-sm font-semibold text-med-text">
                    No se han encontrado usuarios
                  </p>

                  <p className="text-xs text-med-muted mt-1">
                    Prueba con otro nombre o dirección de email.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredUsers.map((user) => {
                    const selected = selectedUserIds.includes(user.id);

                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => toggleUser(user.id)}
                        className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${
                          selected
                            ? "border-med-primary/30 bg-med-primary/5"
                            : "border-med-border bg-white hover:bg-med-surface2"
                        }`}
                      >
                        <div
                          className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center ${
                            selected
                              ? "bg-med-primary/10 text-med-primary"
                              : "bg-med-surface2 text-med-muted"
                          }`}
                        >
                          <UserCircle size={23} weight="duotone" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-med-text truncate">
                            {getUserName(user)}
                          </p>

                          <p className="text-xs text-med-muted truncate">
                            {user.email}
                          </p>
                        </div>

                        <div
                          className={`w-6 h-6 shrink-0 rounded-lg border flex items-center justify-center transition-colors ${
                            selected
                              ? "border-med-primary bg-med-primary text-white"
                              : "border-med-border bg-white"
                          }`}
                        >
                          {selected && <CheckCircle size={18} weight="fill" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-med-border">
              <button
                type="button"
                onClick={closeUsersModal}
                disabled={savingUsers}
                className="rounded-2xl border border-med-border px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-med-surface2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={saveGroupUsers}
                disabled={loadingUsers || savingUsers}
                className="rounded-2xl bg-med-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-med-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingUsers ? "Guardando..." : "Guardar usuarios"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ========================================================
          CONFIRMAR ACTIVAR / DESACTIVAR
      ======================================================== */}

      <ConfirmModal
        open={!!activeTarget}
        onCancel={() => setActiveTarget(null)}
        onConfirm={async () => {
          await toggleGroupActive(activeTarget);
          setActiveTarget(null);
        }}
        title={activeTarget?.active ? "Desactivar grupo" : "Activar grupo"}
        message={
          activeTarget?.active
            ? `¿Seguro que quieres desactivar el grupo "${activeTarget?.name}"? El grupo dejará de estar disponible como destinatario.`
            : `¿Seguro que quieres activar el grupo "${activeTarget?.name}"? El grupo volverá a estar disponible como destinatario.`
        }
        icon={activeTarget?.active ? XCircle : CheckCircle}
        iconContainerClassName={
          activeTarget?.active
            ? "bg-red-100 text-red-500"
            : "bg-green-100 text-green-500"
        }
        confirmText={activeTarget?.active ? "Desactivar" : "Activar"}
        confirmClassName={
          activeTarget?.active
            ? "bg-red-500 hover:bg-red-600"
            : "bg-green-500 hover:bg-green-600"
        }
      />
    </div>
  );
}
